import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import slugify from "slugify";
import axios from "axios";
import {
  composeQueryParams,
  initialDashboardPageMetadata,
  mergeDshbPageMetadataObjects,
  paramsSerializer,
  apiError
} from "../../utils";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";

export const useCollectionOptions = () => {
  const dispatch = useDispatch();
  const collectionOptions = useSelector(selectCollectionOptions);
  const { currentUser } = useSelector(state => state.auth);

  useEffect(() => {
    if (currentUser.role !== "creator" && currentUser.role !== "admin") return;
    if (!collectionOptions.length) {
      dispatch(getCollectionOptionsAsync());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, currentUser]);

  return collectionOptions;
};

export const selectCollectionOptions = state =>
  state.collection.collectionOptions.map(col => ({ id: col.id, label: col.name }));

export const readAllCollectionsForDashboardAsync = createAsyncThunk(
  "collection/readAllForDashboard",
  async (payload = {}, { getState }) => {
    const { metadata } = getState().collection;
    const params = composeQueryParams(payload, metadata);
    const response = await axios.get("/api/admin/collections", { params, paramsSerializer });
    return { collections: response.data };
  }
);

export const getCollectionOptionsAsync = createAsyncThunk(
  "/collection/getCollectionOptions",
  async () => {
    const response = await axios.get(`/api/collection-options`);
    return { collections: response.data };
  }
);

export const upsertCollectionAsync = createAsyncThunk(
  "collection/upsert",
  async (payload, { rejectWithValue, dispatch }) => {
    try {
      const response = await axios.post(`/api/collection`, payload.collection);
      dispatch(getCollectionOptionsAsync());
      return { collection: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);

export const quickCollectionCreateAsync = createAsyncThunk(
  "collection/quickCreate",
  async (payload, { getState, rejectWithValue, dispatch }) => {
    const { frontend, auth } = getState();
    const platformId = payload.PlatformId || frontend.platformData.id;
    const body = {
      name: payload.name,
      slug: slugify(payload.name),
      PlatformId: platformId,
      UserId: auth.currentUser.id
    };
    try {
      const response = await axios.post(`/api/collection`, body);
      dispatch(getCollectionOptionsAsync());
      return { collection: response.data };
    } catch (error) {
      return rejectWithValue(apiError(error));
    }
  }
);

export const deleteCollectionAsync = createAsyncThunk(
  "/collection/delete",
  async (payload, { dispatch }) => {
    await axios.delete(`/api/collection/${payload.collection.id}`);
    dispatch(getCollectionOptionsAsync());
    return { id: payload.collection.id };
  }
);

export const collectionSlice = createSlice({
  name: "collection",
  initialState: {
    loadFailed: false,
    failedLoadArgs: {},
    loading: false,
    error: null,
    collections: [],
    collectionOptions: [],
    metadata: initialDashboardPageMetadata
  },
  reducers: {
    clearError: (state, action) => {
      if (state.error) state.error[action.payload.field] = null;
    },
    clearErrors: state => {
      state.error = null;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(readAllCollectionsForDashboardAsync.pending, state => {
        state.loading = true;
        state.loadFailed = false;
      })
      .addCase(readAllCollectionsForDashboardAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { items, ...metadata } = action.payload.collections || {};
        state.collections = items || [];
        state.metadata = mergeDshbPageMetadataObjects(state.metadata, metadata);
      })
      .addCase(readAllCollectionsForDashboardAsync.rejected, (state, action) => {
        state.loading = false;
        if (action.meta.aborted) return;
        state.loadFailed = true;
        state.failedLoadArgs = action.meta.arg || {};
        state.collections = [];
      })
      .addCase(getCollectionOptionsAsync.fulfilled, (state, action) => {
        state.collectionOptions = action.payload.collections;
      })
      .addCase(upsertCollectionAsync.pending, state => {
        state.loading = true;
      })
      .addCase(quickCollectionCreateAsync.pending, state => {
        state.loading = true;
      })
      .addCase(quickCollectionCreateAsync.fulfilled, state => {
        state.loading = false;
      })
      .addCase(quickCollectionCreateAsync.rejected, state => {
        state.loading = false;
      })
      .addCase(upsertCollectionAsync.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.collection[1]) {
          if (action.payload.collection[0]) state.collections.unshift(action.payload.collection[0]);
        } else {
          state.collections = (state.collections || []).map(collection =>
            collection.id === action.payload.collection[0].id
              ? action.payload.collection[0]
              : collection
          );
        }
      })
      .addCase(upsertCollectionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.fields ?? null;
      })
      .addCase(deleteCollectionAsync.pending, state => {
        state.loading = true;
      })
      .addCase(deleteCollectionAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.collections = state.collections.filter(
          collection => collection.id !== action.payload.id
        );
      })
      .addCase(deleteCollectionAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error;
      });
  }
});
export const { clearError, clearErrors } = collectionSlice.actions;
export default collectionSlice.reducer;
